import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../api/useApiClient';
import { useAuthToken } from '../../hooks/useAuthToken';
import {
  fetchEVData,
  fetchBatteryData,
  deleteEVOverride,
  deleteEVGroup,
  createEVOverride,
  editEVGroup,
  updateAllocations,
} from '../../api/devices';

export const useEVData = (campusId: string) => {
  const client = useApiClient();
  const { hasToken } = useAuthToken();
  return useQuery({
    queryKey: ['devices', campusId, 'ev'],
    queryFn: () => fetchEVData(client, campusId),
    enabled: hasToken && !!campusId,
  });
};

export const useBatteryData = (campusId: string) => {
  const client = useApiClient();
  const { hasToken } = useAuthToken();
  return useQuery({
    queryKey: ['devices', campusId, 'battery'],
    queryFn: () => fetchBatteryData(client, campusId),
    enabled: hasToken && !!campusId,
    refetchInterval: 5000,
  });
};

export const useDeleteEVOverride = (gxId: string) => {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => deleteEVOverride(client, gxId, groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices', gxId, 'ev'] });
    },
  });
};

export const useDeleteEVGroup = (gxId: string) => {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => deleteEVGroup(client, gxId, groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices', gxId, 'ev'] });
    },
  });
};

export const useCreateEVOverride = (gxId: string) => {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => createEVOverride(client, gxId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices', gxId, 'ev'] });
    },
  });
};

export const useEditEVGroup = (gxId: string) => {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => editEVGroup(client, gxId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices', gxId, 'ev'] });
    },
  });
};

export const useUpdateAllocations = (gxId: string) => {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => updateAllocations(client, gxId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices', gxId, 'battery'] });
    },
  });
};
